<template>
  <div class="role-design-page"> 
    <div class="canvas-wrapper">
        <div id="role-design-sandbox-container"></div>
    </div>

    <!-- Talent Card Preview Component -->
    <TalentCardPreview
        v-if="isTalentModalOpen"
        ref="talentCardRef"
        :userId="selectedUserId"
        :userData="selectedUserData"
    />

    <RoleDetailsPopup ref="roleDetailsRef" />
    <RoleEditorModal ref="roleEditorRef" />
    
    <RoleInspectorPopup ref="roleInspector" />
    
  </div>
</template>

<script>
// ✨ Data sanitizer to hide passwords globally
const sanitizeSensitiveData = (obj) => {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime());
    if (Array.isArray(obj)) return obj.map(item => sanitizeSensitiveData(item));
    
    const sanitized = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const lowerKey = key.toLowerCase();
            if (['password', 'salt', 'hash'].includes(lowerKey)) {
                sanitized[key] = '********'; // Mask sensitive data
            } else {
                sanitized[key] = sanitizeSensitiveData(obj[key]);
            }
        }
    }
    return sanitized;
};

import { SandboxView } from './components/SandboxView.js'; 
import Utils from '@/views/org-dashboard/users/components/OrganiGram/src/utility.js';
import { app } from '@/views/org-dashboard/users/components/OrganiGram/src/feathers-socketio.js';
import TalentCardPreview from '@/views/org-dashboard/talent-pool/components/TalentCardPreview.vue';
import RoleInspectorPopup from './components/RoleInspectorPopup.vue';
import RoleEditorModal from './components/RoleEditorModal.vue';
import RoleDetailsPopup from './components/RoleDetailsPopup.vue';

export default {
  name: 'RoleDesign',
  components: {
      TalentCardPreview,
      RoleInspectorPopup,
      RoleDetailsPopup,
      RoleEditorModal
  },
  data() {
    return {
      sandboxInstance: null,
      graphData: [],
      orgName: "Loading...",
      rootId: null,
      primaryColor: "#48A9C5",
      kvdrId: null,
      isInitializing: false,
      isTalentModalOpen: false,
      selectedUserId: "",
      selectedUserData: null,
      syncTimeout: null,
    }
  },
  computed: {
    orgData() {
        return this.$store.state.organization.orgData || {};
    },
    userId() {
        return this.$store.state.auth?.user?._id || null;
    }
  },
  watch: {
    orgData: {
        deep: true,
        handler(newVal) {
            if (newVal && newVal._id && !this.sandboxInstance && !this.isInitializing) {
                this.fetchDataAndInit();
            }
        }
    }
  },
  async mounted() {
    if (this.orgData && this.orgData._id && !this.sandboxInstance) {
        await this.fetchDataAndInit();
    } else {
        const slug = this.$route.params.slug;
        if (slug) {
            try {
                await this.$store.dispatch('organization/fetchOrganizationBySlug', slug);
            } catch (e) {
                console.warn("Could not manually fetch org by slug. Waiting for watcher.", e);
            }
        }
    }
    window.addEventListener('show-talent-card', this.handleShowTalentCard);
    window.addEventListener('show-role-inspector', this.handleShowRoleInspector);
    window.addEventListener('show-role-details', this.handleShowRoleDetails);
    window.addEventListener('open-role-editor', this.handleOpenRoleEditor);

    app.service('kvdr-role').on('patched', this.handleLiveSync);
    app.service('kvdr-role').on('created', this.handleLiveSync);
    app.service('kvdr-role').on('removed', this.handleLiveSync);
  },
  beforeDestroy() {
    if (this.sandboxInstance) {
      this.sandboxInstance.destroy();
    }
    window.removeEventListener('show-talent-card', this.handleShowTalentCard);
    window.removeEventListener('show-role-inspector', this.handleShowRoleInspector);
    window.removeEventListener('open-role-editor', this.handleOpenRoleEditor);

    app.service('kvdr-role').removeListener('patched', this.handleLiveSync);
    app.service('kvdr-role').removeListener('created', this.handleLiveSync);
    app.service('kvdr-role').removeListener('removed', this.handleLiveSync);
    if (this.syncTimeout) clearTimeout(this.syncTimeout);
  },
  methods: {
    handleLiveSync() {
        if (this.syncTimeout) clearTimeout(this.syncTimeout);
        this.syncTimeout = setTimeout(() => {
            if (this.kvdrId) {
                this.fetchDataAndInit();
            }
        }, 500);
    },
    handleOpenRoleEditor(event) {
        const detail = event.detail; 
        
        if (this.$refs.roleEditorRef) {
            const role = detail.role || {};
            
            const defaultKvdr = {
                valueDriver: '', 
                valueTags: [], 
                businessExpressions: [], 
                influenceScore: 1, 
                ownershipClarity: 3,
                otherValueText: ''
            };

            const safeKvdrData = { ...defaultKvdr, ...(role.kvdr || {}) };

            const sanitizedPayload = {
                ...detail, 
                role: {
                    ...role, 
                    kvdr: safeKvdrData
                }
            };
            
            this.$refs.roleEditorRef.open(sanitizedPayload);
        }
    },
    handleShowRoleDetails(event) {
      const { role } = event.detail;
      if (this.$refs.roleDetailsRef) {
         this.$refs.roleDetailsRef.open(role);
      }
    },
    async handleShowTalentCard(event) {
      const userId = event.detail.userId;
      if (!userId) return;

      this.$vs.loading();
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
        const response = await fetch(`https://api.getboarded.tech/users/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const json = await response.json();
        
        this.selectedUserId = userId;
        // ✨ SANITIZE profile payload before passing to the modal
        this.selectedUserData = sanitizeSensitiveData(json);
        this.isTalentModalOpen = true;

        this.$nextTick(() => {
          setTimeout(() => {
            if (this.$refs.talentCardRef) {
               const trigger = this.$refs.talentCardRef.$el.querySelector('i') || this.$refs.talentCardRef.$el;
               if (trigger && trigger.click) trigger.click();
            }
          }, 100);
        });
      } catch (err) {
        console.error("Error loading user:", err);
      } finally {
        this.$vs.loading.close();
      }
    },
    
    handleShowRoleInspector(event) {
        const { nodes } = event.detail;
        if (nodes && nodes.length > 0) {
            this.$nextTick(() => {
                if (this.$refs.roleInspector) {
                    this.$refs.roleInspector.open(nodes);
                }
            });
        }
    },

    async fetchDataAndInit() {
      this.isInitializing = true;
      this.rootId = this.orgData._id;
      this.orgName = this.orgData.name || "Organization";
      this.primaryColor = this.orgData.primaryThemeColor || '#48A9C5';

      try {
          const pythonApiUrl = process.env.VUE_APP_API_URL;
          
          let orgDepartments = [];
          try {
              const token = localStorage.getItem('feathers-jwt') || localStorage.getItem('accessToken');
              const confRes = await fetch(`https://api-core-gateway.getboarded.com/organization/organization-config/${this.rootId}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
              });
              if (confRes.ok) {
                  const confJson = await confRes.json();
                  if (confJson.data && confJson.data.department && confJson.data.department.values) {
                      orgDepartments = confJson.data.department.values;
                  }
              }
          } catch(e) { console.warn("Failed to fetch org config for sandbox departments"); }

          let candidateList = [];
          try {
              const cRes = await fetch(`${pythonApiUrl}/api/v2/organization/${this.rootId}/users`);
              const cData = await cRes.json();
              // ✨ SANITIZE users at fetch 
              candidateList = sanitizeSensitiveData(Array.isArray(cData) ? cData : []);
          } catch (e) {
              console.warn("Could not fetch candidate list from new MongoDB endpoint.", e);
          }

          const res = await this.$store.dispatch("kvdr/findKVDR", this.rootId);
          let rawData = [];

          if (!res || !res.data || !res.data.length) {
              if (!this.userId) throw new Error("Missing User ID");
              const newKvdr = await this.$store.dispatch("kvdr/createKVDR", { userId: this.userId, orgId: this.rootId });
              this.kvdrId = newKvdr._id;
              
              const freshRoles = await app.service('kvdr-role').find({ query: { kvdrId: this.kvdrId, $limit: 1000 } });
              // ✨ SANITIZE roles at fetch
              rawData = sanitizeSensitiveData(freshRoles.data || freshRoles || []);
          } else {
              this.kvdrId = res.data[0]._id;
              
              const freshRoles = await app.service('kvdr-role').find({ query: { kvdrId: this.kvdrId, $limit: 1000 } });
              // ✨ SANITIZE roles at fetch
              rawData = sanitizeSensitiveData(freshRoles.data || freshRoles || []);
          }

          let sqliteMetadataMap = {};
          try {
              const token = localStorage.getItem('feathers-jwt') || localStorage.getItem('accessToken');
              const metaRes = await fetch(`${pythonApiUrl}/api/v2/organization/${this.rootId}/salary-bands`, {
                  headers: { 'Authorization': `Bearer ${token}` }
              });
              if (metaRes.ok) {
                  const metaJson = await metaRes.json();
                  if (metaJson.status === 'success' && metaJson.data) {
                      metaJson.data.forEach(m => {
                          sqliteMetadataMap[m.role_id] = {
                              department: m.department || "",
                              level: m.level || ""
                          };
                      });
                  }
              }
          } catch(e) { console.warn("Could not fetch bulk department mappings", e); }

          let totalHeadcount = 0;
          
          const formattedData = rawData.map(node => {
              let rawProfiles = node.kvdrRoleProfiles || node.profiles || [];
              const validProfiles = [];
              const seenIds = new Set();
              
              rawProfiles.forEach(p => {
                  const pObj = typeof p === 'object' ? p : { id: p };
                  const uid = String(pObj.userId || pObj.user_id || pObj._id || pObj.id);
                  
                  if (!uid || uid === 'undefined' || seenIds.has(uid)) return;
                  
                  let candidateData = candidateList.find(c => String(c.id) === uid || String(c._id) === uid);
                  
                  if (candidateList.length > 0 && !candidateData) {
                      return; 
                  }

                  let statusToCheck = '';
                  if (candidateData) {
                      statusToCheck = String(candidateData.employeeStatus || candidateData.status || '').toUpperCase();
                  } else {
                      candidateData = pObj.userData || pObj;
                      statusToCheck = String(candidateData.status || pObj.status || '').toUpperCase();
                  }

                  if (['DEACTIVATED', 'INACTIVE', 'REMOVED', 'DELETED'].includes(statusToCheck) || 
                      candidateData.isActive === false || pObj.isActive === false) {
                      return; 
                  }
                  
                  seenIds.add(uid);

                  const fName = candidateData.firstName || pObj.firstName || "";
                  const lName = candidateData.lastName || pObj.lastName || "";
                  const combinedName = `${fName} ${lName}`.trim();
                  const fallbackName = candidateData.name || pObj.name || (candidateData.email ? candidateData.email.split('@')[0] : "User Profile");

                  validProfiles.push({
                      ...pObj,
                      ...candidateData, 
                      name: combinedName || fallbackName,
                      id: uid, 
                      userId: uid
                  });
              });

              totalHeadcount += validProfiles.length;
              
              const nodeMeta = sqliteMetadataMap[node.selfId] || {};

            const finalDepartment = nodeMeta.department || node.department || "";
            const finalLevel = nodeMeta.level || node.level || "";
              return {
                  ...node,
              department: finalDepartment,
              level: finalLevel,
              candidateCount: validProfiles.length,
                  profiles: validProfiles, 
                  displaySettings: node.displaySettings || { cc: this.primaryColor, tc: '#fff', bc: '#e5e7eb' }
              };
          });

          const rootDataObj = {
              selfId: this.rootId, 
              title: this.orgName, 
              parentId: null,
              roleSummary: "Organization Design Structure", 
              candidateCount: totalHeadcount, 
              profiles: [], 
              isRoot: true,
              displaySettings: { cc: this.primaryColor, tc: '#fff', bc: '#e5e7eb' }
          };

          this.graphData = [rootDataObj, ...formattedData];

          if (this.sandboxInstance) {
              window.dispatchEvent(new CustomEvent('kvdr-sandbox-refresh', { 
                  detail: { graphData: this.graphData, departments: orgDepartments } 
              }));
              this.isInitializing = false;
              return;
          }

          this.sandboxInstance = new SandboxView({
            container: 'role-design-sandbox-container',
            data: this.graphData,
            departments: orgDepartments, 
            rootId: this.rootId,
            orgId: this.orgData._id, 
            pythonApiUrl: process.env.VUE_APP_API_URL, 
            currentUser: this.$store.state.auth?.user || {}, 
            canApprove: true, 
            displaySettings: { cc: this.primaryColor, tc: '#fff', bc: '#e5e7eb' },
            actions: {
              onUpdate: async (nodeId, newData) => {
                try {
                    const resData = await app.service('kvdr-role').patch(null, newData, {
                        query: { selfId: nodeId }
                    });
                    if (this.$vs) this.$vs.notify({ title: 'Success', text: 'Status updated successfully', color: 'success' });
                    return resData; 
                } catch (err) { 
                    console.error("Update failed", err); 
                    if (this.$vs) this.$vs.notify({ title: 'Error', text: 'Failed to update status.', color: 'danger' });
                    throw err; 
                }
              },
              onDelete: async (node) => {
                const nodeIdToDelete = (typeof node === 'string') ? node : (node.selfId || node.id || node._id);
                if (nodeIdToDelete) {
                    try {
                        await app.service('kvdr-role').remove(null, { query: { selfId: nodeIdToDelete } });
                        this.graphData = this.graphData.filter(n => n.selfId !== nodeIdToDelete && n._id !== nodeIdToDelete && n.id !== nodeIdToDelete);
                        window.dispatchEvent(new CustomEvent('kvdr-sandbox-refresh', { detail: { graphData: this.graphData } }));
                        if (this.$vs) this.$vs.notify({ title: 'Success', text: 'Role deleted successfully', color: 'success' });
                    } catch (err) {
                        console.error("Delete failed", err);
                        if (this.$vs) this.$vs.notify({ title: 'Error', text: 'Failed to delete role.', color: 'danger' });
                    }
                }
              },
              onAdd: async (parentId, popupPayload) => {
                try {
                  const newNodeId = Utils.randomString(24);
                  const validParentId = (parentId === this.rootId) ? "0" : parentId;
                  const apiData = {
                      ...popupPayload, 
                      status: 'published',
                      parentId: validParentId,
                      selfId: newNodeId, 
                      kvdrId: this.kvdrId,
                      displaySettings: { cc: this.primaryColor }
                  };
                  const createdRole = await app.service('kvdr-role').create(apiData);
                  this.graphData.push(createdRole);
                  window.dispatchEvent(new CustomEvent('kvdr-sandbox-refresh', { detail: { graphData: this.graphData } }));
                  this.$vs.notify({ title: 'Success', text: 'Role Created successfully', color: 'success' });
                  return createdRole;
                } catch (err) { 
                  console.error("Create failed", err);
                  this.$vs.notify({ title: 'Error', text: 'Could not create role.', color: 'danger' });
                }
              }
            }
          });
          
          this.isInitializing = false;
          
      } catch (error) {
          console.error("CRITICAL ERROR during initialization:", error);
          this.isInitializing = false;
      }
    }
  }
}
</script>

<style scoped>
.role-design-page {
  height: calc(100vh - 65px); 
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa; 
  overflow: hidden; 
  margin: 0;
  padding: 0;
}

.canvas-wrapper {
  flex: 1;
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

#role-design-sandbox-container {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
}
.canvas-wrapper >>> i, 
.canvas-wrapper >>> .vs-icon,
#role-design-sandbox-container + .talent-card-trigger-icon { 
    display: none !important; 
}

.role-design-page >>> .talent-card-preview-trigger {
    display: none !important;
}

#role-design-sandbox-container {
    background-image: none !important;
}

.role-design-page >>> .vs-component.con-vs-popup {
    z-index: 6000 !important; 
}

.role-design-page >>> .vs-popup--background {
    z-index: 5900 !important;
}
</style>

<style>
.vs-component.con-vs-popup {
    z-index: 50000 !important; 
}

.vs-component.con-vs-popup .vs-popup--background {
    z-index: 1 !important; 
}

.vs-component.con-vs-popup .vs-popup {
    z-index: 10 !important; 
}

.role-design-page [contenteditable="true"] *,
.ql-editor *,
.tox-edit-area * {
    font-family: 'Inter', sans-serif !important;
    color: #475569 !important;
    background-color: transparent !important;
}
</style>